import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getAllUsers } from '../services/userApi';
import { getQuizResultsReport } from '../services/quizApi';
import * as XLSX from 'xlsx'; // Keep this import
import AdminNav from '../components/AdminNav';
import { useAuth } from '../auth/AuthProvider';

const AdminReportsPage = () => {
  const { user } = useAuth();
  const [userId, setUserId] = useState('');
  const [submittedUserId, setSubmittedUserId] = useState('');
  const { data: users, isLoading: isLoadingUsers, error: errorUsers } = useQuery('users', getAllUsers);
  const { data: quizResults, isLoading: isLoadingQuizResults, error: errorQuizResults } = useQuery(
    ['quizResultsReport', submittedUserId],
    () => getQuizResultsReport(submittedUserId),
    {
      enabled: !!submittedUserId,
    }
  );

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(users.map(user => ({
      'User ID': user.userId,
      'Full Name': user.fullName,
      'Email': user.email,
      'Phone': user.phone,
      'Preferred Language': user.preferredLanguage,
      'Country': user.country,
      'Address': user.address,
      'Gender': user.gender,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'users.xlsx');
  };

  const handleFetchResults = (e) => {
    e.preventDefault();
    setSubmittedUserId(userId);
  };

  const exportQuizResultsToExcel = () => {
    if (!quizResults) return;
    const ws = XLSX.utils.json_to_sheet(
      quizResults.map(result => ({
        'User ID': result.userId,
        'Module ID': result.moduleId,
        'Score': result.result,
        'Date Taken': new Date(result.takenAt).toLocaleString(),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Quiz Results');
    XLSX.writeFile(wb, 'quiz-results.xlsx');
  };

  return (
    <div style={{ padding: '20px' }}>
      <AdminNav />
      {/* This link is conditionally rendered for admin users.
          Ideally, this logic would be in a shared header component. */}
      {user && user.role === 'admin' && (
        <div style={{ marginBottom: '20px', marginTop: '20px' }}>
          <Link to="/admin/dashboard" style={{ textDecoration: 'none', color: '#0b3a7a', fontWeight: 'bold' }}>Admin Dashboard</Link>
        </div>
      )}
      <h1>Admin - Reports</h1>
      
      <div style={{ marginBottom: '40px' }}>
        <h2>Quiz Results Report</h2>
        <form onSubmit={handleFetchResults} style={{ marginBottom: '20px' }}>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Enter User ID (optional)"
            style={{ padding: '8px', marginRight: '10px' }}
          />
          <button type="submit" style={{ padding: '8px' }} disabled={isLoadingQuizResults}>
            {isLoadingQuizResults ? 'Fetching...' : 'Get Results'}
          </button>
        </form>

        {isLoadingQuizResults && <div>Loading...</div>}
        {errorQuizResults && <div style={{ color: 'red', marginBottom: '20px' }}>Error: {errorQuizResults.message}</div>}

        {quizResults && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <button onClick={exportQuizResultsToExcel}>Export to Excel</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>User ID</th>
                  <th style={tableHeaderStyle}>Module ID</th>
                  <th style={tableHeaderStyle}>Score</th>
                  <th style={tableHeaderStyle}>Date Taken</th>
                </tr>
              </thead>
              <tbody>
                {quizResults.map((result, index) => (
                  <tr key={index}>
                    <td style={tableCellStyle}>{result.userId}</td>
                    <td style={tableCellStyle}>{result.moduleId}</td>
                    <td style={tableCellStyle}>{result.result}</td>
                    <td style={tableCellStyle}>{new Date(result.takenAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div>
        <h2>User Reports</h2>
        <div style={{ marginBottom: '20px' }}>
          <button onClick={exportToExcel}>Export to Excel</button>
        </div>
        
        {isLoadingUsers && <div>Loading...</div>}
        {errorUsers && <div style={{ color: 'red' }}>Error: {errorUsers.message}</div>}
        
        {users && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>User ID</th>
                <th style={tableHeaderStyle}>Full Name</th>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Phone</th>
                <th style={tableHeaderStyle}>Preferred Language</th>
                <th style={tableHeaderStyle}>Country</th>
                <th style={tableHeaderStyle}>Address</th>
                <th style={tableHeaderStyle}>Gender</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId}>
                  <td style={tableCellStyle}>{user.userId}</td>
                  <td style={tableCellStyle}>{user.fullName}</td>
                  <td style={tableCellStyle}>{user.email}</td>
                  <td style={tableCellStyle}>{user.phone}</td>
                  <td style={tableCellStyle}>{user.preferredLanguage}</td>
                  <td style={tableCellStyle}>{user.country}</td>
                  <td style={tableCellStyle}>{user.address}</td>
                  <td style={tableCellStyle}>{user.gender}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const tableCellStyle = {
  border: '1px solid #ddd',
  padding: '8px',
};

const tableHeaderStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  textAlign: 'left',
  backgroundColor: '#f2f2f2',
};

export default AdminReportsPage;
